import { z } from "zod";
export * from "./agenda-events.js";
export * from "./school-visits.js";
export * from "./payment-terms.js";
export * from "./automation-messages.js";
export * from "./school-brochures.js";
export * from "./balance-message.js";
export const erpEventTypes = [
    "order.upserted",
    "order.deleted",
    "booking.upserted",
    "booking.deleted",
    "booking.attendance_changed",
    "lead.upserted",
    "intent.upserted",
    "confirmation.attempted",
    "agent.upserted",
];
export const erpCommandTypes = [
    "booking.add_note",
    "booking.assign_agent",
    "booking.mark_attended",
    "booking.set_language",
    "booking.update_details",
    "booking.reschedule",
    "booking.change_group",
    "booking.add_paid_addon",
    "booking.record_payment",
    "booking.confirm_order_payment",
    "booking.send_confirmation",
    "booking.prepare_confirmation",
    "booking.delete_permanently",
    "lead.add_note",
    "lead.mark_followup",
    "lead.close",
    "confirmation.resend",
];
export const moneySchema = z.object({
    amount: z.string().regex(/^\d+(?:\.\d{1,2})?$/),
    currency: z.string().length(3).transform((value) => value.toUpperCase()),
});
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const agendaQuerySchema = z.object({
    date: isoDateSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    service: z.string().trim().min(1).max(40).optional(),
}).strict().superRefine((value, context) => {
    if (!value.date && (!value.from || !value.to)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "Provide date or both from and to." });
        return;
    }
    if ((value.from && !value.to) || (!value.from && value.to)) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "from and to must be provided together." });
        return;
    }
    if (value.from && value.to) {
        const from = Date.parse(`${value.from}T00:00:00Z`);
        const to = Date.parse(`${value.to}T00:00:00Z`);
        if (from > to)
            context.addIssue({ code: z.ZodIssueCode.custom, message: "from cannot be after to." });
        if ((to - from) / 86_400_000 > 62) {
            context.addIssue({ code: z.ZodIssueCode.custom, message: "Agenda ranges cannot exceed 63 inclusive days." });
        }
    }
});
export const confirmationDocumentUploadSchema = z.object({
    idempotency_key: z.string().uuid(),
    confirmation_source_id: z.string().min(1).max(191),
    booking_source_id: z.string().min(1).max(191).nullable().optional(),
    order_source_id: z.string().min(1).max(191).nullable().optional(),
    attempt: z.number().int().positive(),
    language: z.enum(["es", "en"]),
    filename: z.string().trim().min(1).max(255).regex(/\.pdf$/i),
    mime_type: z.literal("application/pdf"),
    bytes: z.number().int().positive().max(8 * 1024 * 1024),
    sha256: z.string().regex(/^[a-f0-9]{64}$/i).transform((value) => value.toLowerCase()),
    content_base64: z.string().min(8).max(11_200_000),
    sent_at: z.string().datetime({ offset: true }),
}).strict();
export const crmChannels = ["whatsapp", "web", "manual"];
export const crmServices = ["balloon", "tour", "school"];
export const crmOpportunityStages = ["new", "conversation", "quote", "awaiting", "won", "lost"];
export const crmCustomerCreateSchema = z.object({
    display_name: z.string().trim().min(1).max(190),
    phone_e164: z.string().trim().regex(/^\+[1-9]\d{7,14}$/).optional(),
    email: z.string().trim().email().max(190).transform((value) => value.toLowerCase()).optional(),
    language: z.enum(["es", "en"]).default("es"),
    channel: z.enum(crmChannels),
    commercial_intent: z.boolean(),
    service_type: z.enum(crmServices).optional(),
    requested_date: isoDateSchema.optional(),
    /** "month": the customer only knows the month; requested_date is then the 1st of that month. */
    requested_date_precision: z.enum(["day", "month"]).optional(),
    people_count: z.number().int().positive().max(500).optional(),
    note: z.string().trim().max(2_000).optional(),
    idempotency_key: z.string().uuid().optional(),
    /** Attach the sale to this existing WhatsApp chat instead of opening a new thread. */
    whatsapp_conversation_id: z.string().uuid().optional(),
}).strict().superRefine((value, context) => {
    if (!value.phone_e164 && !value.email) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "Provide phone_e164 or email." });
    }
    if (value.commercial_intent && !value.service_type) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "Commercial intent requires service_type." });
    }
});
/** Trip facts of a sale (date and people), edited from the case header. */
export const crmOpportunityDetailsSchema = z.object({
    expected_version: z.number().int().positive(),
    requested_date: isoDateSchema.nullable().optional(),
    requested_date_precision: z.enum(["day", "month"]).optional(),
    people_count: z.number().int().positive().max(5_000).nullable().optional(),
    /** School group: teachers and companions besides the students (people_count). */
    teachers_count: z.number().int().min(0).max(1_000).nullable().optional(),
    companions_count: z.number().int().min(0).max(1_000).nullable().optional(),
    /** Fixes a sale filed under the wrong service (e.g. a balloon flight saved as a school visit). */
    service_type: z.enum(["balloon", "tour", "school"]).optional(),
}).strict();
/** Forward-only stage step after a panel action outside commerce (e.g. a quote sent by WhatsApp). */
export const crmOpportunityAdvanceSchema = z.object({
    stage: z.enum(["quote", "awaiting"]),
    reason: z.string().trim().min(3).max(200),
}).strict();
export const crmOpportunityMutationSchema = z.object({
    expected_version: z.number().int().positive(),
    stage: z.enum(crmOpportunityStages),
    lost_reason: z.string().trim().min(3).max(500).optional(),
    next_follow_up_at: z.string().datetime({ offset: true }).nullable().optional(),
}).strict().superRefine((value, context) => {
    if (value.stage === "lost" && !value.lost_reason) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "Lost opportunities require lost_reason." });
    }
    if (value.stage !== "lost" && value.lost_reason) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "lost_reason is only valid for lost opportunities." });
    }
});
/** Lost reason used when a sale is archived for inactivity; a new message or a booking revives it. */
export const CRM_ARCHIVE_REASON = "Archivada: sin actividad en 30 días";
export const crmFollowupCreateSchema = z.object({
    opportunity_id: z.string().uuid(),
    due_at: z.string().datetime({ offset: true }),
    note: z.string().trim().min(1).max(2_000),
    draft_body: z.string().trim().min(1).max(4_000).optional(),
    /** What happens to the current pending follow-up: done, or replaced by the new date (default). */
    previous: z.enum(["completed", "cancelled"]).optional(),
}).strict();
export const crmDraftSendSchema = z.object({
    expected_version: z.number().int().positive(),
}).strict();
export const crmCustomerUpdateSchema = z.object({
    expected_version: z.number().int().positive(),
    display_name: z.string().trim().min(1).max(190),
    language: z.enum(["es", "en"]),
    phone_e164: z.string().trim().regex(/^\+[1-9]\d{7,14}$/).nullable(),
    email: z.string().trim().email().max(190).transform((value) => value.toLowerCase()).nullable(),
}).strict().superRefine((value, context) => {
    if (!value.phone_e164 && !value.email) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: "Provide phone_e164 or email." });
    }
});
export const crmNoteCreateSchema = z.object({
    body: z.string().trim().min(1).max(2_000),
    opportunity_id: z.string().uuid().nullable().optional(),
}).strict();
export const crmFollowupUpdateSchema = z.discriminatedUnion("status", [
    z.object({
        expected_version: z.number().int().positive(),
        status: z.literal("pending"),
        due_at: z.string().datetime({ offset: true }),
        note: z.string().trim().min(1).max(2_000),
    }).strict(),
    z.object({ expected_version: z.number().int().positive(), status: z.literal("completed") }).strict(),
    z.object({ expected_version: z.number().int().positive(), status: z.literal("cancelled") }).strict(),
]);
export const crmCustomerMergeSchema = z.object({
    customer_ids: z.tuple([z.string().uuid(), z.string().uuid()]),
    expected_versions: z.tuple([z.number().int().positive(), z.number().int().positive()]),
}).strict().superRefine((value, context) => {
    if (value.customer_ids[0] === value.customer_ids[1]) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["customer_ids"], message: "Customers must be different." });
    }
});
export const erpEventEnvelopeSchema = z.object({
    event_id: z.string().uuid(),
    event_type: z.enum(erpEventTypes),
    aggregate_type: z.enum(["order", "booking", "lead", "intent", "confirmation", "agent"]),
    aggregate_id: z.string().min(1).max(191),
    aggregate_version: z.number().int().positive(),
    occurred_at: z.string().datetime({ offset: true }),
    schema_version: z.literal(1),
    source: z.literal("rx-reservas-ruta-magica"),
    payload: z.record(z.unknown()),
});
export const erpCommandEnvelopeSchema = z.object({
    command_id: z.string().uuid(),
    command_type: z.enum(erpCommandTypes),
    aggregate_id: z.string().min(1).max(191),
    expected_version: z.number().int().nonnegative(),
    requested_at: z.string().datetime({ offset: true }),
    actor: z.object({
        erp_user_id: z.string().uuid(),
        name: z.string().min(1).max(190),
        role: z.enum(["owner", "manager", "operator", "accounting"]),
    }),
    payload: z.record(z.unknown()),
});
export const rolePermissions = {
    owner: ["*"],
    manager: [
        "dashboard.read",
        "orders.read",
        "finance.read",
        "bookings.read",
        "confirmations.read",
        "bookings.note",
        "bookings.attend",
        "bookings.language",
        "bookings.edit",
        "bookings.add_paid_addon",
        "bookings.record_payment",
        "confirmations.send",
        "confirmations.share",
        "leads.followup",
        "confirmations.resend",
        "team.read",
        "audit.read",
        "system.read",
        "whatsapp.read",
        "whatsapp.reply",
        "whatsapp.handoff",
        "crm.read",
        "crm.write",
        "crm.reply",
        "crm.analytics.read",
    ],
    operator: [
        "dashboard.read",
        "bookings.read",
        "bookings.note",
        "bookings.attend",
        "bookings.language",
        "leads.followup",
        "confirmations.read",
        "confirmations.share",
        "whatsapp.read",
        "whatsapp.reply",
        "whatsapp.handoff",
        "crm.read",
        "crm.write",
        "crm.reply",
    ],
    accounting: [
        "dashboard.read",
        "orders.read",
        "finance.read",
        "bookings.read",
        "confirmations.read",
        "audit.read",
        "crm.analytics.read",
    ],
};
export * from "./crm-automation.js";
